import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header/Header';
import { fetchAPI } from '../api';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function PlayerDetail() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0); // 0=Global, 1=Championnat, 2=Coupe, 3=Amical

  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - DÉTAIL JOUEUR';
    
    loadPlayerDetail();
  }, [playerId]);

  const loadPlayerDetail = async () => {
    try {
      setLoading(true);
      
      // Récupérer les infos du joueur
      const playersData = await fetchAPI('/players');
      const playerInfo = playersData.find(p => p.rowid === parseInt(playerId));
      setPlayer(playerInfo);
      
      // Récupérer les stats détaillées du joueur
      const statsResponse = await fetch(`${API_URL}/player-detail/${playerId}`);
      const statsData = await statsResponse.json();
      setStats(statsData);
      
    } catch (error) {
      console.error('Erreur chargement détails joueur:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredStats = () => {
    if (!stats || !stats.byCompetition) return null;
    
    const competitions = ['Global', 'Championnat', 'Coupe', 'Amical'];
    const selectedCompetition = competitions[selectedTab];
    
    if (selectedTab === 0) {
      // Global : additionner toutes les compétitions
      const total = {
        competition: 'Global',
        matchs: 0,
        buts: 0,
        passes: 0,
        cartons_jaunes: 0,
        cartons_rouges: 0
      };
      
      stats.byCompetition.forEach(comp => {
        total.matchs += comp.matchs;
        total.buts += comp.buts;
        total.passes += comp.passes;
        total.cartons_jaunes += comp.cartons_jaunes;
        total.cartons_rouges += comp.cartons_rouges;
      });
      
      return total;
    } else {
      // Retourner la compétition sélectionnée
      return stats.byCompetition.find(c => c.competition === selectedCompetition) || {
        competition: selectedCompetition,
        matchs: 0,
        buts: 0,
        passes: 0,
        cartons_jaunes: 0,
        cartons_rouges: 0
      };
    }
  };

  if (loading) {
    return (
      <div>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (!player || !stats) {
    return (
      <div>
        <Header />
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6">Joueur non trouvé</Typography>
        </Box>
      </div>
    );
  }

  return (
    <div>
      {/* Header avec bouton retour */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 2, 
        bgcolor: '#fff',
        borderBottom: '1px solid #e0e0e0'
      }}>
        <IconButton 
          onClick={() => navigate('/joueurs')} 
          sx={{ 
            p: 1,
            minWidth: 'auto',
            width: 'auto'
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <img src="/assets/logo_esma.png" alt="logo" style={{ height: '40px', marginLeft: 'auto', marginRight: 'auto' }} />
        <Box sx={{ width: '48px' }} /> {/* Spacer pour centrer le logo */}
      </Box>

      {/* Nom du joueur et graphique titularisation */}
      <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {player.firstname} {player.lastname}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {player.label}
            </Typography>
          </Box>
          
          {/* Graphique circulaire titularisation */}
          {stats?.titularisation && stats.titularisation.total_matchs > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle
                    cx="40"
                    cy="40"
                    r="30"
                    fill="none"
                    stroke="#e0e0e0"
                    strokeWidth="8"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="30"
                    fill="none"
                    stroke="#4caf50"
                    strokeWidth="8"
                    strokeDasharray={`${(stats.titularisation.titulaire / stats.titularisation.total_matchs) * 188.4} 188.4`}
                    strokeDashoffset="0"
                    transform="rotate(-90 40 40)"
                    strokeLinecap="round"
                  />
                </svg>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '14px' }}>
                    {Math.round((stats.titularisation.titulaire / stats.titularisation.total_matchs) * 100)}%
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ mt: 0.5, fontSize: '10px', textAlign: 'center' }}>
                Titulaire
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ width: '100%' }}>
        
        <Box sx={{ p: 2 }}>

        {/* Onglets de filtrage */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="fullWidth"
          >
            <Tab label="GLOBAL" />
            <Tab label="CHAMP" />
            <Tab label="COUPE" />
            <Tab label="AMIC" />
          </Tabs>
        </Box>

        {/* Statistiques filtrées */}
        {getFilteredStats() && (() => {
          const comp = getFilteredStats();
          return (
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {comp.matchs}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Matchs
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {comp.buts}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Buts
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {comp.passes}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Passes D.
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {comp.cartons_jaunes}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          C. Jaunes
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                          {comp.cartons_rouges}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          C. Rouges
                        </Typography>
                      </Box>
                    </Grid>
              </Grid>
            </CardContent>
          </Card>
          );
        })()}

        <Divider sx={{ my: 3 }} />

        {/* Moyennes par match */}
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          Moyennes par match
        </Typography>

        {stats.averages && stats.averages.length > 0 ? (
          <TableContainer component={Paper} elevation={2}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Statistique</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Moyenne</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.averages.map((avg) => (
                  <TableRow key={avg.stat_label}>
                    <TableCell>{avg.stat_label}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={avg.average.toFixed(2)} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">{avg.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Aucune statistique détaillée disponible
            </Typography>
          </Box>
        )}
        </Box>
      </Box>
    </div>
  );
}

